import { useEffect, useRef, useState, useCallback } from 'react';
import { isEqual } from 'lodash';

type UseSyncedStateResult<T> = [
  T,
  React.Dispatch<React.SetStateAction<T>>,
  () => void,
  boolean, // isDirty
];

export function useSyncedStateWithVersion<T>(externalValue: T, version: number | string): UseSyncedStateResult<T> {
  const [localState, setLocalState] = useState<T>(externalValue);
  const [isDirty, setIsDirty] = useState(false);

  const lastVersionRef = useRef(version);
  const lastValueRef = useRef(externalValue);

  // Sync on version change
  useEffect(() => {
    if (lastVersionRef.current !== version) {
      lastVersionRef.current = version;
      lastValueRef.current = externalValue;
      setLocalState(externalValue);
      setIsDirty(false); // Reset dirty flag on fresh sync
    }
  }, [externalValue, version]);

  // Track changes to local state and compare with external (initial) value
  useEffect(() => {
    setIsDirty(!isEqual(localState, lastValueRef.current));
  }, [localState]);

  // Manual reset
  const resetLocalState = useCallback(() => {
    setLocalState(lastValueRef.current);
    setIsDirty(false);
  }, []);

  return [localState, setLocalState, resetLocalState, isDirty];
}
